<template>
    <div v-if="documents.length > 0" class="car-documents">
        <div
            v-for="(document, index) in documents"
            :key="document.id || index"
            class="car-document"
        >
            <img v-svg-inline :src="getIconByFileType(document.content_type)" />

            <a
                class="car-document__info"
                @click.stop="downloadCarDocument(document.id, document.name)"
            >
                <div class="car-document__type">{{ typeTitle }}</div>
                <div class="car-document__name">{{ document.name }}</div>
            </a>

            <div v-if="canEdit" class="car-document__actions">
                <!-- update -->
                <button
                    class="car-document__action"
                    aria-label="Update document"
                    @click="onUpdateDocument(index)"
                >
                    <img v-svg-inline src="@/assets/icons/ui/update.svg" />
                </button>

                <!-- delete -->
                <button
                    class="car-document__action delete"
                    aria-label="Delete document"
                    @click="onDeleteDocument(index)"
                >
                    <img v-svg-inline src="@/assets/icons/ui/close.svg" />
                </button>
            </div>
        </div>

        <!-- добавления нового документа (кнопка + скрытый input) -->
        <div v-if="canEdit" class="car-document-add">
            <div class="car-document-add__title">{{ typeTitle }}</div>

            <!-- прикрепить документ -->
            <button class="car-document-add__action" @click="triggerFileInput">
                <img
                    v-svg-inline
                    class="car-document-add__action-icon"
                    src="@/assets/icons/ui/plus.svg"
                />

                <span class="car-document-add__action-text">{{ buttonLabel }}</span>
            </button>

            <!-- Скрытый input для загрузки -->
            <input
                ref="fileInput"
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.png"
                style="display: none;"
                @change="onFileChange"
            />
        </div>
    </div>

    <div
        v-if="documents.length === 0 && ifNoDocumentsShowThis === 'file-not-uploaded'"
        class="car-documents"
    >
        <div v-if="!canEdit" class="car-document">
            <div class="car-document__info">
                <div class="car-document__type">{{ typeTitle }}</div>
                <div class="car-document__name">Файл не загружен</div>
            </div>
        </div>

        <!-- добавления нового документа (кнопка + скрытый input) -->
        <div v-if="canEdit" class="car-document-add">
            <div class="car-document-add__title">{{ typeTitle }}</div>

            <!-- прикрепить документ -->
            <button class="car-document-add__action" @click="triggerFileInput">
                <img
                    v-svg-inline
                    class="car-document-add__action-icon"
                    src="@/assets/icons/ui/plus.svg"
                />

                <span class="car-document-add__action-text">{{ buttonLabel }}</span>
            </button>

            <!-- Скрытый input для загрузки -->
            <input
                ref="fileInput"
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.png"
                style="display: none;"
                @change="onFileChange"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import noPhotoImage from '@/assets/images/cars/no-photo.webp';
    import PdfFileIcon from '@/assets/icons/file-pdf.svg';
    import JpegFileIcon from '@/assets/icons/file-jpeg.svg';
    import JpgFileIcon from '@/assets/icons/file-jpg.svg';
    import PngFileIcon from '@/assets/icons/file-png.svg';
    import WebpFileIcon from '@/assets/icons/file-webp.svg';
    import VideoFile from '@/assets/icons/file-video.svg';

    import { computed, PropType, ref } from 'vue';

    import { CarDocument } from '@/types/car/car-document.type.ts';

    import useDocumentDownloader from '@/utils/useDocumentDownloader.ts';

    /**
     * Принимаем список документов (carDocuments) + тексты кнопок.
     * canEdit может быть пропом или вычисляться иначе.
     */
    const props = defineProps({
        carDocuments: {
            type: Array as PropType<CarDocument[]>,
            required: true,
        },
        buttonLabel: {
            type: String,
            default: 'Прикрепить документ',
        },
        typeTitle: {
            type: String,
            required: true,
        },
        ifNoDocumentsShowThis: {
            type: String as PropType<'file-not-uploaded' | 'nothing'>,
            default: null,
        },
    });

    const emit = defineEmits([
        'update-document',
        'delete-document',
        // 'add-document',
        // какой файл выбрал пользователь
        'fileSelected',
    ]);

    const { downloadCarDocument } = useDocumentDownloader();

    const canEdit = ref(false);
    const fileInput = ref<HTMLInputElement | null>(null);

    const documents = computed(() => props.carDocuments || []);

    function triggerFileInput(): void {
        fileInput.value?.click();
    }

    /** сообщаем родителю (пока оставлю) */
    function onFileChange(e: Event): void {
        const target = e.target as HTMLInputElement;
        if (!target.files?.length) {
            return;
        }

        const file = target.files[0];
        // сбрасываем value, чтобы можно было выбрать тот же файл
        target.value = '';
        // Передаем файл родителю
        emit('fileSelected', file);
    }

    /** «обновить», «удалить» и «добавить» */
    function onUpdateDocument(index: number): void {
        emit('update-document', index);
    }

    function onDeleteDocument(index: number): void {
        emit('delete-document', index);
    }

    // function onAddDocument(): void {
    //     emit('add-document');
    // }

    function getIconByFileType(fileType?: string): string {
        if (!fileType) {
            return noPhotoImage;
        }

        if (fileType === 'application/pdf') {
            return PdfFileIcon;
        }
        else if (fileType === 'image/jpeg') {
            return JpegFileIcon;
        }
        else if (fileType === 'image/jpg') {
            return JpgFileIcon;
        }
        else if (fileType === 'image/png') {
            return PngFileIcon;
        }
        else if (fileType === 'image/webp') {
            return WebpFileIcon;
        }
        else if (fileType.startsWith('video/')) {
            return VideoFile;
        }

        return noPhotoImage;
    }
</script>

<style scoped lang="scss">
    @use './CarDocuments.scss' as *;
</style>
